/**
 * Perceived Performance Enhancements
 * Makes the site FEEL faster through visual tricks and optimistic updates
 *
 * CORE WEB VITALS OPTIMIZED:
 * - MutationObserver scoped to specific containers (INP fix)
 * - Ripple effect uses transform instead of left/top (INP fix)
 * - View Transitions code removed (was disabled, causing dead code)
 */

(function() {
  'use strict';

  // ==========================================================================
  // 1. LQIP (Low Quality Image Placeholder) - Blur-up effect
  // ==========================================================================


  // ==========================================================================
  // ATC & checkout button feedback
  // De hoofd-ATC-flow (custom add-to-cart bundle) en de checkout-knoppen gaven
  // geen zichtbare laadstatus, waardoor bezoekers dubbelklikten. Dit blok zet
  // direct bij de klik een spinner + disabled, en herstelt op het einde van de
  // eerstvolgende cart/add-request (of na een vangnet-timeout).
  // ==========================================================================

  function initAtcFeedback() {
    var BUSY = 'atc-busy';
    var DONE = 'atc-done';
    var pending = [];

    function restore(button, success) {
      if (!button) return;
      button.classList.remove(BUSY);
      button.removeAttribute('aria-busy');
      if (success) {
        button.classList.add(DONE);
        setTimeout(function () {
          button.classList.remove(DONE);
          button.disabled = false;
        }, 1200);
      } else {
        button.disabled = false;
      }
    }

    document.addEventListener('click', function (event) {
      var target = event.target.closest ? event.target.closest('.js-add-to-cart, .js-sticky-desktop-atc-btn, .js-sticky-atc-btn') : null;
      if (!target || target.disabled) return;
      target.classList.add(BUSY);
      target.setAttribute('aria-busy', 'true');
      // Niet vóór de andere click-handlers uitzetten: pas in de volgende tick.
      setTimeout(function () { target.disabled = true; }, 0);
      pending.push(target);
      // Vangnet: nooit langer dan 8s op slot (bijv. validatiefout zonder request).
      setTimeout(function () {
        var index = pending.indexOf(target);
        if (index !== -1) { pending.splice(index, 1); restore(target, false); }
      }, 8000);
    }, true);

    // Checkout-links en -knoppen: spinner tot de navigatie het overneemt.
    document.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('a[href*="/checkout"], button[name="checkout"]') : null;
      if (!link) return;
      if (link.classList.contains(BUSY)) { event.preventDefault(); return; }
      link.classList.add(BUSY);
      link.setAttribute('aria-busy', 'true');
    }, true);
    window.addEventListener('pageshow', function () {
      Array.prototype.forEach.call(document.querySelectorAll('a[href*="/checkout"].' + BUSY + ', button[name="checkout"].' + BUSY), function (el) {
        el.classList.remove(BUSY);
        el.removeAttribute('aria-busy');
      });
    });

    // Afronden zodra de cart/add-request klaar is.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlString = typeof url === 'string' ? url : (url && url.url) || '';
      var isCartAdd = urlString.indexOf('cart/add') !== -1;
      var result = originalFetch.apply(this, arguments);
      if (isCartAdd && pending.length) {
        var buttons = pending.splice(0, pending.length);
        result.then(function (response) {
          buttons.forEach(function (button) { restore(button, response && response.ok); });
        }).catch(function () {
          buttons.forEach(function (button) { restore(button, false); });
        });
      }
      return result;
    };
  }

  function initBlurUpImages() {
    // Find all images with data-src (lazy loaded) or loading="lazy"
    // NOTE: Excluded .block-product__media img to prevent flickering on collection pages
    const images = document.querySelectorAll('.product-hero-main__image');

    images.forEach(img => {
      // Skip if already processed
      if (img.dataset.blurProcessed) return;
      img.dataset.blurProcessed = 'true';

      // Add blur class initially
      img.classList.add('img-blur-up');

      // When image loads, remove blur
      if (img.complete) {
        img.classList.add('img-blur-up--loaded');
      } else {
        img.addEventListener('load', function() {
          // Small delay for smooth transition
          requestAnimationFrame(() => {
            this.classList.add('img-blur-up--loaded');
          });
        }, { once: true });

        // Fallback: remove blur after 3 seconds regardless
        setTimeout(() => {
          img.classList.add('img-blur-up--loaded');
        }, 3000);
      }
    });
  }

  // ==========================================================================
  // 2. Optimistic Cart Updates
  // ==========================================================================

  function initOptimisticCart() {
    // Intercept add-to-cart
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // Optimistically update cart count
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
      updateCartCountOptimistic(quantity);

      // Add instant visual feedback
      const button = form.querySelector('[type="submit"], .js-add-to-cart');
      if (button) {
        button.classList.add('btn-clicked');
        setTimeout(() => button.classList.remove('btn-clicked'), 150);
      }
    }, true);

    // Also handle AJAX cart adds
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // Parse quantity from body if JSON
        try {
          if (options?.body) {
            const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
            const quantity = body.quantity || body.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 1;
            updateCartCountOptimistic(quantity);
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
      return originalFetch.apply(this, arguments);
    };
  }

  function updateCartCountOptimistic(addQuantity) {
    const cartCountElements = document.querySelectorAll('.js-cart-count, .cart-count, [data-cart-count]');

    cartCountElements.forEach(el => {
      const current = parseInt(el.textContent) || 0;
      const newCount = current + addQuantity;

      // Animate the number change
      el.classList.add('cart-count-bump');
      el.textContent = newCount;

      setTimeout(() => {
        el.classList.remove('cart-count-bump');
      }, 300);
    });
  }

  // ==========================================================================
  // 3. Micro-interactions - Instant click feedback
  // INP OPTIMIZED: Uses transform instead of left/top, deferred to rAF
  // ==========================================================================

  function initMicroInteractions() {
    // Add ripple effect to buttons - INP optimized version
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button, .btn, .btn-icon, [role="button"]');
      if (!button) return;

      // Defer DOM work to next frame to not block INP
      requestAnimationFrame(() => {
        const ripple = document.createElement('span');
        ripple.className = 'click-ripple';

        // Use cached dimensions if available
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        // INP FIX: Use transform instead of left/top (avoids layout thrashing)
        ripple.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          transform: translate(${x}px, ${y}px);
        `;

        // Ensure button can contain ripple
        if (getComputedStyle(button).position === 'static') {
          button.style.position = 'relative';
        }
        button.style.overflow = 'hidden';
        button.appendChild(ripple);

        // Use animationend instead of setTimeout for cleanup
        ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
      });
    }, { passive: true });

    // Add touch feedback for mobile
    document.addEventListener('touchstart', (e) => {
      const interactive = e.target.closest('a, button, .btn, [role="button"], .block-product');
      if (interactive) {
        interactive.classList.add('touch-active');
      }
    }, { passive: true });

    document.addEventListener('touchend', () => {
      document.querySelectorAll('.touch-active').forEach(el => {
        el.classList.remove('touch-active');
      });
    }, { passive: true });
  }

  // ==========================================================================
  // 4. Scroll-linked animations (content reveal)
  // ==========================================================================

  function initScrollAnimations() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    // Observe elements that should animate in
    // NOTE: Excluded .block-product and collection page containers to prevent flickering
    document.querySelectorAll('.block-collection, section > .container').forEach(el => {
      // Skip collection page - products should load immediately without animation
      if (el.closest('#products-grid') || el.querySelector('#products-grid') || el.querySelector('.js-product-block')) {
        return;
      }
      if (!el.classList.contains('no-animate')) {
        el.classList.add('animate-on-scroll');
        observer.observe(el);
      }
    });
  }

  // ==========================================================================
  // Initialize all enhancements
  // ==========================================================================

  function init() {
    initBlurUpImages();
  initAtcFeedback();
    initOptimisticCart();
    initMicroInteractions();
    initScrollAnimations();

    // Debug info (only in development)
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      const blurImages = document.querySelectorAll('.img-blur-up');
      const animateElements = document.querySelectorAll('.animate-on-scroll');
      console.log('✨ Perceived performance loaded:');
      console.log('   - Blur-up images:', blurImages.length);
      console.log('   - Scroll animations:', animateElements.length);
      console.log('   - Optimistic cart: active');
      console.log('   - Micro-interactions: active');
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ==========================================================================
  // INP OPTIMIZED: Scoped MutationObserver for dynamic content
  // Only watches specific containers instead of entire body
  // ==========================================================================

  function initScopedObserver() {
    // Target specific containers that load dynamic content
    const dynamicContainers = [
      '#products-grid',           // Collection product grid (AJAX filtering)
      '.js-upsell-slider',        // Upsell products
      '.slick-track',             // Slick carousel slides
      '.drawer-cart__items'       // Mini cart items
    ];

    const contentObserver = new MutationObserver((mutations) => {
      // Check if any mutation added images
      const hasNewImages = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 && (
            node.tagName === 'IMG' ||
            (node.querySelector && node.querySelector('img'))
          )
        )
      );

      if (hasNewImages) {
        // Defer to idle time to not block interactions
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => initBlurUpImages(), { timeout: 500 });
        } else {
          requestAnimationFrame(initBlurUpImages);
        }
      }
    });

    // Observe only specific containers (much lighter than body)
    dynamicContainers.forEach(selector => {
      const container = document.querySelector(selector);
      if (container) {
        contentObserver.observe(container, { childList: true, subtree: true });
      }
    });

    // Fallback: observe body but with minimal options if no specific containers
    if (!dynamicContainers.some(sel => document.querySelector(sel))) {
      contentObserver.observe(document.body, { childList: true, subtree: false });
    }
  }

  // Initialize scoped observer after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScopedObserver);
  } else {
    initScopedObserver();
  }

})();
